// File Filters component - Ultra Modern & Compact Design
import React, { useState, useEffect, useRef } from 'react';
import { FileType, FileStatus } from '@types';
import { cn } from '@utils/classNames';
import { FILE_TYPE_COLORS } from '@config/constants';
import { Star, Music, Disc, Filter, ChevronDown, ChevronUp, X } from 'lucide-react';
import { ModalShell } from '@components/ui/ModalShell';

interface FileFiltersProps {
  selectedType: FileType | 'all' | 'starred';
  onTypeChange: (type: FileType | 'all' | 'starred') => void;
  selectedStatus?: FileStatus | 'all';
  onStatusChange?: (status: FileStatus | 'all') => void;
  selectedBpmRange?: string;
  onBpmRangeChange?: (range: string) => void;
  selectedKey?: string;
  onKeyChange?: (key: string) => void;
}

export const FileFilters: React.FC<FileFiltersProps> = ({
  selectedType,
  onTypeChange,
  selectedStatus = 'all',
  onStatusChange,
  selectedBpmRange = 'all',
  onBpmRangeChange,
  selectedKey = 'all',
  onKeyChange,
}) => {
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowFilterMenu(false);
      }
    };

    if (showFilterMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showFilterMenu]);

  const typeFilters = [
    { label: 'All', value: 'all' as const, icon: null, shortLabel: 'All' },
    { label: 'Starred', value: 'starred' as const, icon: 'star', color: '#fbbf24', shortLabel: '⭐' },
    { label: 'Beats', value: FileType.BEAT, icon: null, shortLabel: 'Beats' },
    { label: 'Samples', value: FileType.SAMPLE, icon: null, shortLabel: 'Samples' },
    { label: 'Loops', value: FileType.LOOP, icon: null, shortLabel: 'Loops' },
    { label: 'One-shots', value: FileType.ONESHOT, icon: null, shortLabel: 'Shots' },
  ];

  const statusFilters = [
    { label: 'All', value: 'all' as const, emoji: '📁', shortLabel: 'All' },
    { label: 'WIP', value: FileStatus.WIP, emoji: '🚧', shortLabel: 'WIP' },
    { label: 'Draft', value: FileStatus.DRAFT, emoji: '🟡', shortLabel: 'Draft' },
    { label: 'Tagged', value: FileStatus.TAGGED, emoji: '🏷️', shortLabel: 'Tagged' },
    { label: 'Untagged', value: FileStatus.UNTAGGED, emoji: '✨', shortLabel: 'Clean' },
    { label: 'Ready', value: FileStatus.READY, emoji: '🟢', shortLabel: 'Ready' },
    { label: 'Sold', value: FileStatus.SOLD, emoji: '✅', shortLabel: 'Sold' },
  ];

  const bpmRanges = [
    { label: 'All BPM', value: 'all' },
    { label: '60-90', value: '60-90' },
    { label: '90-120', value: '90-120' },
    { label: '120-140', value: '120-140' },
    { label: '140-160', value: '140-160' },
    { label: '160-180', value: '160-180' },
    { label: '180+', value: '180+' },
  ];

  const musicalKeys = [
    { label: 'All Keys', value: 'all' },
    { label: 'C', value: 'C' },
    { label: 'C#', value: 'C#' },
    { label: 'D', value: 'D' },
    { label: 'D#', value: 'D#' },
    { label: 'E', value: 'E' },
    { label: 'F', value: 'F' },
    { label: 'F#', value: 'F#' },
    { label: 'G', value: 'G' },
    { label: 'G#', value: 'G#' },
    { label: 'A', value: 'A' },
    { label: 'A#', value: 'A#' },
    { label: 'B', value: 'B' },
  ];

  const hasActiveFilters = selectedStatus !== 'all' || selectedBpmRange !== 'all' || selectedKey !== 'all';
  const activeFilterCount = [selectedStatus !== 'all', selectedBpmRange !== 'all', selectedKey !== 'all'].filter(Boolean).length;
  const hasTypeFilter = selectedType !== 'all';

  const clearAllFilters = () => {
    onStatusChange?.('all');
    onBpmRangeChange?.('all');
    onKeyChange?.('all');
  };

  return (
    <div className="space-y-2">
      {/* Mobile: Compact Filter Button with Dropdown */}
      <div className="@explorer:hidden relative">
        <button
          onClick={() => setShowFilterMenu(!showFilterMenu)}
          className={cn(
            'w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg font-inter font-medium text-sm transition-all duration-150 touch-manipulation min-h-[44px]',
            'border border-white/10 hover:border-white/20',
            (hasTypeFilter || hasActiveFilters)
              ? 'bg-primary/20 text-primary hover:bg-primary/30'
              : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80'
          )}
        >
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4" />
            <span>Filters</span>
            {(hasTypeFilter || hasActiveFilters) && (
              <span className="px-1.5 py-0.5 bg-primary text-black rounded-full text-[10px] font-bold min-w-[18px] text-center">
                {[hasTypeFilter, hasActiveFilters].filter(Boolean).length}
              </span>
            )}
          </div>
          {showFilterMenu ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </button>

        {/* Filter Dropdown Menu */}
        {showFilterMenu && (
          <ModalShell
            open={showFilterMenu}
            onClose={() => setShowFilterMenu(false)}
            mode="sheet"
            panelClassName="inset-x-0 bottom-0 bg-dark-secondary/95 backdrop-blur-lg border border-white/10 rounded-t-2xl shadow-2xl p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] space-y-3 animate-fade-in max-h-[85dvh] overflow-y-auto"
          >
            <div
              ref={menuRef}
              className="inset-x-0 bottom-0 bg-dark-secondary/95 backdrop-blur-lg border border-white/10 rounded-t-2xl shadow-2xl p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] space-y-3 animate-fade-in max-h-[85dvh] overflow-y-auto"
            >
            {/* Type Filters */}
            <div>
              <p className="text-xs text-white/60 font-inter font-bold uppercase mb-2">File Type</p>
              <div className="flex items-center gap-1.5 flex-wrap">
                {typeFilters.map((filter) => {
                  const isActive = selectedType === filter.value;
                  const color = filter.value === 'starred' 
                    ? filter.color 
                    : filter.value !== 'all' 
                      ? FILE_TYPE_COLORS[filter.value] 
                      : '#30d870';
                  
                  return (
                    <button
                      key={filter.value}
                      onClick={() => onTypeChange(filter.value)}
                      className={cn(
                        'relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-codec font-bold text-xs transition-all duration-150 touch-manipulation flex-shrink-0 min-h-[36px]',
                        'border',
                        isActive
                          ? 'text-black shadow-md scale-[1.02] border-transparent'
                          : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white border-white/10 hover:border-white/20 active:scale-95'
                      )}
                      style={isActive ? { 
                        backgroundColor: color,
                        boxShadow: `0 2px 8px 0 ${color}30`
                      } : undefined}
                    >
                      {filter.icon === 'star' ? (
                        <Star className={cn(
                          "w-3.5 h-3.5 transition-all",
                          isActive ? "fill-black" : "fill-yellow-400/50"
                        )} />
                      ) : filter.value !== 'all' && filter.value !== 'starred' && (
                        <div
                          className={cn(
                            "w-2 h-2 rounded-full flex-shrink-0 transition-all",
                            isActive && "ring-1 ring-black/20"
                          )}
                          style={{ backgroundColor: isActive ? 'black' : color }}
                        />
                      )}
                      <span className="whitespace-nowrap">{filter.shortLabel}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Status Filters */}
            {onStatusChange && (
              <>
                <div className="w-full h-px bg-white/10" />
                <div>
                  <p className="text-xs text-white/60 font-inter font-bold uppercase mb-2">Status</p>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {statusFilters.map((filter) => {
                      const isActive = selectedStatus === filter.value;
                      
                      return (
                        <button
                          key={filter.value}
                          onClick={() => onStatusChange(filter.value)}
                          className={cn(
                            'group flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-inter font-medium text-xs transition-all duration-150 touch-manipulation flex-shrink-0 min-h-[36px]',
                            'border',
                            isActive
                              ? 'bg-gradient-to-r from-primary/90 to-[#00ff61]/90 text-black shadow-md border-transparent scale-[1.02]'
                              : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80 border-white/10 hover:border-white/20 active:scale-95'
                          )}
                        >
                          <span className="text-sm leading-none transition-transform group-hover:scale-110">
                            {filter.emoji}
                          </span>
                          <span className="whitespace-nowrap">{filter.shortLabel}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            {/* Advanced Filters */}
            {(onBpmRangeChange || onKeyChange) && (
              <>
                <div className="w-full h-px bg-white/10" />
                <div>
                  <p className="text-xs text-white/60 font-inter font-bold uppercase mb-2">Advanced</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {onBpmRangeChange && (
                      <div className="relative flex-1 min-w-[120px]">
                        <select
                          value={selectedBpmRange}
                          onChange={(e) => onBpmRangeChange(e.target.value)}
                          className={cn(
                            'w-full pl-8 pr-3 py-2 rounded-lg font-inter font-medium text-xs transition-all duration-150 cursor-pointer appearance-none touch-manipulation min-h-[36px]',
                            'border',
                            selectedBpmRange !== 'all'
                              ? 'bg-gradient-to-r from-primary/90 to-[#00ff61]/90 text-black shadow-md border-transparent'
                              : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80 border-white/10 hover:border-white/20'
                          )}
                        >
                          {bpmRanges.map((range) => (
                            <option key={range.value} value={range.value} className="bg-dark-secondary text-white">
                              {range.label}
                            </option>
                          ))}
                        </select>
                        <Music className={cn(
                          "absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none transition-colors",
                          selectedBpmRange !== 'all' ? 'text-black' : 'text-white/40'
                        )} />
                      </div>
                    )}

                    {onKeyChange && (
                      <div className="relative flex-1 min-w-[110px]">
                        <select
                          value={selectedKey}
                          onChange={(e) => onKeyChange(e.target.value)}
                          className={cn(
                            'w-full pl-8 pr-3 py-2 rounded-lg font-codec font-bold text-xs transition-all duration-150 cursor-pointer appearance-none touch-manipulation min-h-[36px]',
                            'border',
                            selectedKey !== 'all'
                              ? 'bg-gradient-to-r from-beat/90 to-[#ff6b6b]/90 text-black shadow-md border-transparent'
                              : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80 border-white/10 hover:border-white/20'
                          )}
                        >
                          {musicalKeys.map((key) => (
                            <option key={key.value} value={key.value} className="bg-dark-secondary text-white">
                              {key.label}
                            </option>
                          ))}
                        </select>
                        <Disc className={cn(
                          "absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none transition-colors",
                          selectedKey !== 'all' ? 'text-black' : 'text-white/40'
                        )} />
                      </div>
                    )}

                    {hasActiveFilters && (
                      <button
                        onClick={clearAllFilters}
                        className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg font-inter font-medium text-xs transition-all duration-150 touch-manipulation border border-white/10 hover:border-white/20 bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80 active:scale-95 min-h-[36px]"
                      >
                        <X className="w-3.5 h-3.5" />
                        <span>Clear</span>
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}

            <div className="pt-2 border-t border-white/10 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={clearAllFilters}
                className="inline-flex items-center justify-center rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs font-inter font-medium text-white/70 hover:bg-white/10 min-h-[40px] touch-manipulation"
              >
                Clear all
              </button>
              <button
                type="button"
                onClick={() => setShowFilterMenu(false)}
                className="inline-flex items-center justify-center rounded-lg border border-primary/40 bg-primary/15 px-3 py-2 text-xs font-inter font-semibold text-primary hover:bg-primary/20 min-h-[40px] touch-manipulation"
              >
                Close
              </button>
            </div>
            </div>
          </ModalShell>
        )}
      </div>

      {/* Desktop: Original Filter Bar */}
      <div className="hidden @explorer:flex items-center gap-2 flex-wrap">
        {/* Type Filters */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {typeFilters.map((filter) => {
            const isActive = selectedType === filter.value;
            const color = filter.value === 'starred' 
              ? filter.color 
              : filter.value !== 'all' 
                ? FILE_TYPE_COLORS[filter.value] 
                : '#30d870';
            
            return (
              <button
                key={filter.value}
                onClick={() => onTypeChange(filter.value)}
                className={cn(
                  'relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-codec font-bold text-xs transition-all duration-150 touch-manipulation flex-shrink-0',
                  'border',
                  isActive
                    ? 'text-black shadow-md scale-[1.02] border-transparent'
                    : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white border-white/10 hover:border-white/20 active:scale-95'
                )}
                style={isActive ? { 
                  backgroundColor: color,
                  boxShadow: `0 2px 8px 0 ${color}30`
                } : undefined}
              >
                {filter.icon === 'star' ? (
                  <Star className={cn(
                    "w-3 h-3 transition-all",
                    isActive ? "fill-black" : "fill-yellow-400/50"
                  )} />
                ) : filter.value !== 'all' && filter.value !== 'starred' && (
                  <div
                    className={cn(
                      "w-2 h-2 rounded-full flex-shrink-0 transition-all",
                      isActive && "ring-1 ring-black/20"
                    )}
                    style={{ backgroundColor: isActive ? 'black' : color }}
                  />
                )}
                <span className="whitespace-nowrap">{filter.shortLabel}</span>
              </button>
            );
          })}
        </div>

        {/* Divider */}
        {(onStatusChange || onBpmRangeChange || onKeyChange) && (
          <div className="w-px h-6 bg-white/10" />
        )}

        {/* Status Filters */}
        {onStatusChange && (
          <div className="flex items-center gap-1.5 flex-wrap">
            {statusFilters.map((filter) => {
              const isActive = selectedStatus === filter.value;
              
              return (
                <button
                  key={filter.value}
                  onClick={() => onStatusChange(filter.value)}
                  className={cn(
                    'group flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-inter font-medium text-xs transition-all duration-150 touch-manipulation flex-shrink-0',
                    'border',
                    isActive
                      ? 'bg-gradient-to-r from-primary/90 to-[#00ff61]/90 text-black shadow-md border-transparent scale-[1.02]'
                      : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80 border-white/10 hover:border-white/20 active:scale-95'
                  )}
                >
                  <span className="text-sm leading-none transition-transform group-hover:scale-110">
                    {filter.emoji}
                  </span>
                  <span className="whitespace-nowrap hidden sm:inline">{filter.label}</span>
                  <span className="whitespace-nowrap sm:hidden">{filter.shortLabel}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Advanced Filters Button */}
        {(onBpmRangeChange || onKeyChange) && (
          <>
            <div className="w-px h-6 bg-white/10" />
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className={cn(
                'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-inter font-medium text-xs transition-all duration-150 touch-manipulation',
                'border border-white/10 hover:border-white/20',
                hasActiveFilters
                  ? 'bg-primary/20 text-primary hover:bg-primary/30'
                  : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80'
              )}
            >
              <Filter className="w-3 h-3" />
              <span className="hidden sm:inline">Filters</span>
              {activeFilterCount > 0 && (
                <span className="px-1.5 py-0.5 bg-primary text-black rounded-full text-[10px] font-bold min-w-[18px] text-center">
                  {activeFilterCount}
                </span>
              )}
              {showAdvanced ? (
                <ChevronUp className="w-3 h-3 ml-0.5" />
              ) : (
                <ChevronDown className="w-3 h-3 ml-0.5" />
              )}
            </button>
          </>
        )}
      </div>

      {/* Advanced Filters Dropdown - Mobile First */}
      {showAdvanced && (onBpmRangeChange || onKeyChange) && (
        <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-white/10 animate-fade-in">
          {/* BPM Dropdown - Mobile First */}
          {onBpmRangeChange && (
            <div className="relative group">
              <select
                value={selectedBpmRange}
                onChange={(e) => onBpmRangeChange(e.target.value)}
                className={cn(
                  'pl-8 md:pl-8 pr-3 md:pr-3 py-2 md:py-1.5 rounded-lg font-inter font-medium text-xs md:text-xs transition-all duration-150 cursor-pointer appearance-none min-w-[120px] md:min-w-[100px] touch-manipulation min-h-[44px] md:min-h-0',
                  'border',
                  selectedBpmRange !== 'all'
                    ? 'bg-gradient-to-r from-primary/90 to-[#00ff61]/90 text-black shadow-md border-transparent'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80 border-white/10 hover:border-white/20'
                )}
              >
                {bpmRanges.map((range) => (
                  <option key={range.value} value={range.value} className="bg-dark-secondary text-white">
                    {range.label}
                  </option>
                ))}
              </select>
              <Music className={cn(
                "absolute left-2.5 md:left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 md:w-3 md:h-3 pointer-events-none transition-colors",
                selectedBpmRange !== 'all' ? 'text-black' : 'text-white/40'
              )} />
            </div>
          )}

          {/* Key Dropdown - Mobile First */}
          {onKeyChange && (
            <div className="relative group">
              <select
                value={selectedKey}
                onChange={(e) => onKeyChange(e.target.value)}
                className={cn(
                  'pl-8 md:pl-8 pr-3 md:pr-3 py-2 md:py-1.5 rounded-lg font-codec font-bold text-xs md:text-xs transition-all duration-150 cursor-pointer appearance-none min-w-[110px] md:min-w-[90px] touch-manipulation min-h-[44px] md:min-h-0',
                  'border',
                  selectedKey !== 'all'
                    ? 'bg-gradient-to-r from-beat/90 to-[#ff6b6b]/90 text-black shadow-md border-transparent'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80 border-white/10 hover:border-white/20'
                )}
              >
                {musicalKeys.map((key) => (
                  <option key={key.value} value={key.value} className="bg-dark-secondary text-white">
                    {key.label}
                  </option>
                ))}
              </select>
              <Disc className={cn(
                "absolute left-2.5 md:left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 md:w-3 md:h-3 pointer-events-none transition-colors",
                selectedKey !== 'all' ? 'text-black' : 'text-white/40'
              )} />
            </div>
          )}

          {/* Clear Filters Button - Mobile First */}
          {hasActiveFilters && (
            <>
              <div className="w-px h-6 md:h-6 bg-white/10" />
              <button
                onClick={clearAllFilters}
                className="flex items-center gap-1.5 px-2.5 md:px-2.5 py-2 md:py-1.5 rounded-lg font-inter font-medium text-xs md:text-xs transition-all duration-150 touch-manipulation border border-white/10 hover:border-white/20 bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80 active:scale-95 min-h-[44px] md:min-h-0"
              >
                <X className="w-4 h-4 md:w-3 md:h-3" />
                <span className="hidden sm:inline">Clear</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
